import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import SelectionList from './SelectionList'
import { Input } from '@/components/ui/input'
import ResetIcon from '@/components/icons/ResetIcon'
import { formatCurrencyAutoLocale } from '@/utils/tools'

const StageBudgetHeader = ({ stage, showHeaderText, stagesWithAudiences, store, hasAudiences, currency }) => {
    const stageData = store.getStageData(stage.id)
    const stageShare = stageData.share || 0
    const stageAmount = (store.totalSmartBudget * stageShare) / 100

    const handleStageShareChange = ({ target }) => {
        const newShare = parseFloat(target.value)

        if (target.value === '') {
            store.setStageShare(stage.id, 0, stagesWithAudiences)
            return
        }

        if (isNaN(newShare)) return

        store.setStageShare(stage.id, newShare, stagesWithAudiences)
    }

    // Helper function to format input value - only show decimals if needed
    const formatInputValue = (value) => {
        if (value === 0) return ''
        return value % 1 === 0 ? value.toString() : value.toString()
    }

    return (
        <div className="tw:flex tw:flex-col tw:gap-2">
            {showHeaderText && (
                <button
                    onClick={store.resetSmartBudgets}
                    className={cn(
                        'tw:text-blue-300 tw:!text-sm tw:flex tw:items-center tw:gap-1 tw:cursor-pointer tw:justify-end',
                        'tw:opacity-0 tw:transition-opacity tw:duration-300 tw:ease-in-out',
                        store.hasEdits && 'tw:opacity-100',
                    )}
                >
                    <ResetIcon className="tw:size-4" />
                    Reset to Defaults
                </button>
            )}
            <div className="tw:flex tw:gap-2">
                <div className="tw:flex tw:flex-col tw:w-22 tw:max-w-22 tw:gap-2">
                    {showHeaderText && (
                        <span className="tw:w-full tw:text-right tw:text-sm tw:font-semibold">Budget Percentage</span>
                    )}

                    {hasAudiences && (
                        <Input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            placeholder="0"
                            value={formatInputValue(stageShare)}
                            onChange={handleStageShareChange}
                            className="tw:h-8 tw:w-full tw:text-right"
                        />
                    )}
                </div>
                <div className="tw:flex tw:flex-col tw:w-20 tw:max-w-20 tw:gap-2 tw:text-right">
                    {showHeaderText && <span className="tw:w-full tw:text-sm tw:font-semibold">Budget Amount</span>}
                    <div className="tw:flex tw:items-center tw:justify-end tw:h-8">
                        {hasAudiences && (
                            <span className="tw:text-sm">
                                {formatCurrencyAutoLocale({ amount: stageAmount || 0, showCode: true, currency })}
                            </span>
                        )}
                        {!hasAudiences && (
                            <span className="tw:text-sm">
                                {formatCurrencyAutoLocale({ amount: 0, showCode: true, currency })}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {/* Error message display */}
            {store.budgetError && (
                <div className="tw:flex tw:items-center tw:gap-2 tw:px-2 tw:py-1 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded tw:text-red-700 tw:text-xs">
                    <span className="tw:flex-1">{store.budgetError}</span>
                    <button
                        onClick={store.clearBudgetError}
                        className="tw:text-red-500 hover:tw:text-red-700 tw:font-semibold"
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    )
}

const ReadOnlyStage = ({ stage }) => {
    return (
        <div key={stage.id} className="tw:flex tw:gap-4 tw:flex-col">
            <div className="tw:flex tw:justify-between tw:items-center tw:px-6">
                <div className="tw:inline-flex tw:justify-between tw:items-end tw:w-full">
                    <div className="tw:inline-flex tw:gap-2 tw:items-center tw:cursor-pointer">
                        <div className="tw:size-3 tw:rounded-full tw:border-2 tw:border-gray-200"></div>
                        <span className="tw:text-sm tw:font-semibold tw:cursor-pointer">{stage.name}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

const Stage = ({
    stage,
    audiences,
    availableStages,
    audienceSelections,
    stagesWithAudiences,
    store,
    currency,
    minDailyBudget,
    showHeaderText,
}) => {
    const isAvailable = availableStages.includes(stage.id)
    const [isOpen, setIsOpen] = useState(true)
    const selectedAudienceIds = audienceSelections[stage.id] || []
    const selectedAudience = audiences.filter((audience) => selectedAudienceIds.includes(audience.id))
    const toggleOpen = () => setIsOpen(!isOpen)

    if (!isAvailable) return <ReadOnlyStage stage={stage} />

    return (
        <div key={stage.id} className="tw:flex tw:gap-2 tw:flex-col">
            <div className="tw:flex tw:justify-between tw:items-center tw:px-6">
                <div className="tw:inline-flex tw:justify-between tw:items-end tw:w-full">
                    <div
                        onClick={toggleOpen}
                        className="tw:inline-flex tw:gap-2 tw:h-8 tw:items-center tw:cursor-pointer"
                    >
                        {selectedAudienceIds.length > 0 && (
                            <div className="tw:w-4">
                                <ChevronDown
                                    className={cn(
                                        'tw:size-4 tw:cursor-pointer tw:text-blue-300 tw:transition-transform tw:duration-300',
                                        isOpen && 'tw:rotate-180',
                                    )}
                                />
                            </div>
                        )}
                        <div className="tw:size-3 tw:rounded-full" style={{ backgroundColor: stage.color }}></div>
                        <span className="tw:text-sm tw:font-semibold tw:cursor-pointer">{stage.name}</span>
                    </div>
                    <StageBudgetHeader
                        stage={stage}
                        store={store}
                        currency={currency}
                        showHeaderText={showHeaderText}
                        audienceSelections={audienceSelections}
                        stagesWithAudiences={stagesWithAudiences}
                        hasAudiences={selectedAudienceIds.length > 0}
                    />
                </div>
            </div>

            <div
                className={cn(
                    'tw:overflow-hidden tw:transition-all tw:duration-300 tw:ease-in-out',
                    isOpen ? 'tw:max-h-96 tw:opacity-100' : 'tw:max-h-0 tw:opacity-0',
                )}
            >
                <div
                    className={cn(
                        'tw:flex tw:flex-col tw:divide-y tw:mb-4 ',
                        selectedAudienceIds.length > 0 && 'tw:border-t tw:border-b',
                    )}
                >
                    <SelectionList
                        minDailyBudget={minDailyBudget}
                        stage={stage}
                        color={stage.color}
                        audiences={selectedAudience}
                        store={store}
                        currency={currency}
                    />
                </div>
            </div>
        </div>
    )
}

const Stages = ({ allStages, audiences, availableStages, audienceSelections, store, currency, minDailyBudget }) => {
    const stagesWithAudiences = []

    Object.keys(audienceSelections).forEach((stageId) => {
        if (audienceSelections[stageId].length) stagesWithAudiences.push(stageId)
    })

    return (
        <div className="tw:flex tw:flex-col tw:gap-2">
            {allStages.map((stage, index) => (
                <Stage
                    stage={stage}
                    store={store}
                    key={stage.id}
                    currency={currency}
                    audiences={audiences}
                    showHeaderText={index === 0}
                    minDailyBudget={minDailyBudget}
                    availableStages={availableStages}
                    audienceSelections={audienceSelections}
                    stagesWithAudiences={stagesWithAudiences}
                />
            ))}
        </div>
    )
}

export default Stages
